import Papa from 'papaparse';
import { parseWfDate } from './date';

// Columns accepted from the uploaded CSV — mirrors the RequestWithOppContext shape
// (types/index.ts), using the NEW field names after domain model correction.
// Excludes fields that are always derived rather than user-provided (req_age, opp_market_area).
export const UNTRACKED_CSV_COLUMNS = [
  'opp_id', 'acc_name', 'user_sc_name', 'req_status', 'opp_adjusted_commit',
  'opp_region', 'req_sub_region', 'opp_close_q', 'req_offering', 'req_name',
  'req_id', 'req_products', 'req_entry', 'req_completion', 'opp_segment',
  'opp_segment_sales', 'opp_gnarr_grand_total', 'opp_gnarr', 'req_created_q',
  'req_created_fy', 'opp_status', 'req_closed_fy', 'req_closed_q',
] as const;

const REQUIRED_COLUMNS = ['opp_id', 'req_id'] as const;

// Columns accepted from the uploaded touchpoint CSV — mirrors the
// TouchpointWithOppContext shape (types/index.ts). Excludes tp_comment_text,
// which is always derived from tp_solution_comments (see normalizeTouchpoint).
export const TOUCHPOINT_CSV_COLUMNS = [
  'tp_id', 'opp_id', 'acc_name', 'user_sc_name', 'tp_name', 'tp_type',
  'tp_status', 'tp_entry', 'tp_date', 'tp_completion', 'tp_actual_start_date',
  'tp_created_fy', 'tp_created_q', 'tp_solution_comments', 'tp_products', 'opp_age',
  'opp_gnarr_grand_total', 'opp_gnarr', 'opp_close_q', 'opp_region',
  'opp_segment', 'opp_segment_sales', 'opp_market_area',
] as const;

const REQUIRED_TOUCHPOINT_COLUMNS = ['tp_id', 'opp_id'] as const;

export interface ParsedCsvRow {
  raw: Record<string, string>;
  index: number;
  issues: string[];
}

export interface ParsedCsvResult {
  rows: ParsedCsvRow[];
  columns: string[];
  fileErrors: string[];
}

function validateRow(raw: Record<string, string>): string[] {
  const issues: string[] = [];
  for (const col of REQUIRED_COLUMNS) {
    if (!raw[col]?.trim()) issues.push(`missing ${col}`);
  }
  // req_entry/req_completion must be dd/MM/yy — this is what daysBetween()/req_age
  // computation expects uniformly for every request, synced or uploaded.
  if (raw.req_entry?.trim() && !parseWfDate(raw.req_entry)) issues.push('req_entry is not dd/MM/yy');
  if (raw.req_completion?.trim() && !parseWfDate(raw.req_completion)) issues.push('req_completion is not dd/MM/yy');
  return issues;
}

function validateTouchpointRow(raw: Record<string, string>): string[] {
  const issues: string[] = [];
  for (const col of REQUIRED_TOUCHPOINT_COLUMNS) {
    if (!raw[col]?.trim()) issues.push(`missing ${col}`);
  }
  if (raw.tp_entry?.trim() && !parseWfDate(raw.tp_entry)) issues.push('tp_entry is not dd/MM/yy');
  if (raw.tp_date?.trim() && !parseWfDate(raw.tp_date)) issues.push('tp_date is not dd/MM/yy');
  if (raw.tp_completion?.trim() && !parseWfDate(raw.tp_completion)) issues.push('tp_completion is not dd/MM/yy');
  return issues;
}

function parseCsv(file: File, validate: (raw: Record<string, string>) => string[]): Promise<ParsedCsvResult> {
  return new Promise(resolve => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim(),
      complete: result => {
        const columns = result.meta.fields ?? [];
        const rows: ParsedCsvRow[] = result.data.map((raw, index) => ({
          raw,
          index,
          issues: validate(raw),
        }));
        const fileErrors = result.errors.map(e => `Row ${e.row ?? '?'}: ${e.message}`);
        resolve({ rows, columns, fileErrors });
      },
      error: err => {
        resolve({ rows: [], columns: [], fileErrors: [err.message] });
      },
    });
  });
}

export function parseUntrackedCsv(file: File): Promise<ParsedCsvResult> {
  return parseCsv(file, validateRow);
}

export function parseUntrackedTouchpointCsv(file: File): Promise<ParsedCsvResult> {
  return parseCsv(file, validateTouchpointRow);
}
